// Canned sample reports used to give brand-new users an instant "aha" before
// their first real call is analyzed. Pure static data — no backend.
//
// The app renders call reports in two shapes:
//   - CallCard (list view)  reads metrics at the TOP level of the call object
//   - CallDetailPage        reads them under call.analysis
// so each variant carries BOTH: the analysis object plus the same metric fields
// spread onto the top level. Prose is localized (en/es); numbers are shared.

const SHARED = {
  strong: {
    score: 87,
    talkRatio: { repTalkPct: 45, inSweetSpot: true, sweetSpotRange: "40-50%" },
    questionCount: { count: 8, target: "5-10", inTarget: true },
    interruptionRate: { ratePct: 4, moments: [] },
    silenceRatio: { silencePct: 11, inSweetSpot: true, sweetSpotRange: "5-15%" },
    nextSteps: { hasNextSteps: true },
    closurePct: 78,
  },
  rough: {
    score: 54,
    talkRatio: { repTalkPct: 68, inSweetSpot: false, sweetSpotRange: "40-50%" },
    questionCount: { count: 2, target: "5-10", inTarget: false },
    interruptionRate: { ratePct: 18, moments: [] },
    silenceRatio: { silencePct: 3, inSweetSpot: false, sweetSpotRange: "5-15%" },
    nextSteps: { hasNextSteps: false },
    closurePct: 31,
  },
};

const CONTENT = {
  en: {
    strong: {
      title: "Discovery Call — Acme Corp",
      summary:
        "Strong discovery call. You led with great questions, kept talk time balanced, and locked in a concrete next step. Tighten the close by quantifying impact earlier.",
      closureReason:
        "Prospect named a budget owner and asked about onboarding timelines — clear buying signals with a scheduled follow-up.",
      speakerBreakdown: [
        { speaker: "You", pct: 45 },
        { speaker: "Sarah (Acme)", pct: 41 },
        { speaker: "Mark (Acme)", pct: 14 },
      ],
      nextStepDescription: "Send pricing one-pager and book a technical demo for next Tuesday.",
      keyMoments: [
        { type: "discovery", timestamp: "3:120", text: "Asked how the team measures success today — surfaced their core KPI." },
        { type: "objection", timestamp: "11:40", text: "“We already use a spreadsheet for this.” — handled by reframing time cost." },
        { type: "buying-signal", timestamp: "18:05", text: "“How fast could we get this live for the team?”" },
        { type: "closing", timestamp: "24:30", text: "Agreed to a technical demo and named the budget owner." },
      ],
      improvementTips: [
        "Quantify the cost of their current spreadsheet workflow earlier in the call.",
        "Confirm decision timeline before moving to the demo.",
        "Trial-close once more after the buying signal at 18:05.",
      ],
      transcript: [
        { speaker: "You", startTime: 12, text: "Thanks for making the time, Sarah. Before I show anything — how does your team measure a good month today?" },
        { speaker: "Sarah (Acme)", startTime: 28, text: "Honestly it's mostly gut feel and a shared spreadsheet. It gets messy." },
        { speaker: "You", startTime: 41, text: "That's super common. What happens when something falls through the cracks?" },
      ],
    },
    rough: {
      title: "Discovery Call — Globex",
      summary:
        "You talked for most of the call and pitched before understanding the problem. Ask more questions, let pauses breathe, and always agree a next step before hanging up.",
      closureReason:
        "Prospect stayed non-committal and no follow-up was scheduled. Low intent signals throughout.",
      speakerBreakdown: [
        { speaker: "You", pct: 68 },
        { speaker: "Dana (Globex)", pct: 32 },
      ],
      nextStepDescription: null,
      keyMoments: [
        { type: "risk", timestamp: "2:15", text: "Jumped into the pitch before asking about their goals." },
        { type: "objection", timestamp: "9:50", text: "“This sounds expensive.” — moved past it without addressing value." },
        { type: "risk", timestamp: "21:10", text: "Call ended with no agreed next step." },
      ],
      improvementTips: [
        "Open with 3-4 discovery questions before presenting anything.",
        "When you hear a price objection, anchor on value before responding.",
        "Always agree a concrete next step before ending the call.",
      ],
      transcript: [
        { speaker: "You", startTime: 8, text: "So let me walk you through everything our platform can do — there's a lot here." },
        { speaker: "Dana (Globex)", startTime: 30, text: "Okay... sure." },
        { speaker: "You", startTime: 33, text: "Right, so first there's the dashboard, then reporting, then integrations..." },
      ],
    },
  },
  es: {
    strong: {
      title: "Llamada de descubrimiento — Acme Corp",
      summary:
        "Excelente llamada de descubrimiento. Lideraste con buenas preguntas, equilibraste el tiempo de habla y cerraste un próximo paso concreto. Mejora el cierre cuantificando el impacto antes.",
      closureReason:
        "El prospecto mencionó a quién aprueba el presupuesto y preguntó por los tiempos de implementación — señales de compra claras con seguimiento agendado.",
      speakerBreakdown: [
        { speaker: "Tú", pct: 45 },
        { speaker: "Sarah (Acme)", pct: 41 },
        { speaker: "Mark (Acme)", pct: 14 },
      ],
      nextStepDescription: "Enviar el resumen de precios y agendar una demo técnica para el próximo martes.",
      keyMoments: [
        { type: "discovery", timestamp: "3:20", text: "Preguntaste cómo mide el éxito el equipo hoy — saliste con su KPI principal." },
        { type: "objection", timestamp: "11:40", text: "“Ya usamos una hoja de cálculo para esto.” — manejado replanteando el costo de tiempo." },
        { type: "buying-signal", timestamp: "18:05", text: "“¿Qué tan rápido podríamos ponerlo en marcha para el equipo?”" },
        { type: "closing", timestamp: "24:30", text: "Acordaron una demo técnica y mencionó al responsable del presupuesto." },
      ],
      improvementTips: [
        "Cuantifica antes el costo de su flujo actual con hojas de cálculo.",
        "Confirma el plazo de decisión antes de pasar a la demo.",
        "Haz un cierre de prueba tras la señal de compra en 18:05.",
      ],
      transcript: [
        { speaker: "Tú", startTime: 12, text: "Gracias por tu tiempo, Sarah. Antes de mostrarte nada — ¿cómo mide tu equipo un buen mes hoy?" },
        { speaker: "Sarah (Acme)", startTime: 28, text: "La verdad es que es intuición y una hoja compartida. Se vuelve un desorden." },
        { speaker: "Tú", startTime: 41, text: "Es muy común. ¿Qué pasa cuando algo se escapa?" },
      ],
    },
    rough: {
      title: "Llamada de descubrimiento — Globex",
      summary:
        "Hablaste la mayor parte de la llamada y presentaste antes de entender el problema. Haz más preguntas, deja respirar las pausas y siempre acuerda un próximo paso antes de colgar.",
      closureReason:
        "El prospecto se mantuvo sin compromiso y no se agendó seguimiento. Señales de bajo interés durante toda la llamada.",
      speakerBreakdown: [
        { speaker: "Tú", pct: 68 },
        { speaker: "Dana (Globex)", pct: 32 },
      ],
      nextStepDescription: null,
      keyMoments: [
        { type: "risk", timestamp: "2:15", text: "Entraste al pitch antes de preguntar por sus objetivos." },
        { type: "objection", timestamp: "9:50", text: "“Suena caro.” — lo dejaste pasar sin abordar el valor." },
        { type: "risk", timestamp: "21:10", text: "La llamada terminó sin un próximo paso acordado." },
      ],
      improvementTips: [
        "Abre con 3-4 preguntas de descubrimiento antes de presentar nada.",
        "Ante una objeción de precio, ancla en el valor antes de responder.",
        "Siempre acuerda un próximo paso concreto antes de terminar la llamada.",
      ],
      transcript: [
        { speaker: "Tú", startTime: 8, text: "Déjame mostrarte todo lo que hace nuestra plataforma — hay muchísimo aquí." },
        { speaker: "Dana (Globex)", startTime: 30, text: "Okey... claro." },
        { speaker: "Tú", startTime: 33, text: "Bien, primero está el panel, luego los reportes, luego las integraciones..." },
      ],
    },
  },
};

// Fixed timestamp so the sample never looks stale relative to "now".
const SAMPLE_TIMESTAMP = "2026-06-05T14:15:00.000Z";

function buildVariant(lang, variant) {
  const shared = SHARED[variant];
  const c = (CONTENT[lang] || CONTENT.en)[variant];

  const talkRatio = { ...shared.talkRatio, speakerBreakdown: c.speakerBreakdown };
  const nextSteps = { ...shared.nextSteps, description: c.nextStepDescription || undefined };

  const analysis = {
    score: shared.score,
    summary: c.summary,
    talkRatio,
    questionCount: shared.questionCount,
    interruptionRate: shared.interruptionRate,
    silenceRatio: shared.silenceRatio,
    nextSteps,
    keyMoments: c.keyMoments,
    improvementTips: c.improvementTips,
    closurePct: shared.closurePct,
    closureReason: c.closureReason,
  };

  return {
    id: "sample",
    title: c.title,
    timestamp: SAMPLE_TIMESTAMP,
    status: "completed",
    isSample: true,
    // top-level metrics for CallCard (list view)
    score: shared.score,
    summary: c.summary,
    talkRatio,
    questionCount: shared.questionCount,
    interruptionRate: shared.interruptionRate,
    silenceRatio: shared.silenceRatio,
    nextSteps,
    // nested for CallDetailPage
    analysis,
    transcript: c.transcript,
  };
}

/** Returns { strong, rough } sample call objects for the given language. */
export function getSampleReports(lang = "en") {
  const l = lang && lang.startsWith("es") ? "es" : "en";
  return {
    strong: buildVariant(l, "strong"),
    rough: buildVariant(l, "rough"),
  };
}
